import { useMemo, useState, type FormEvent } from 'react';
import { Info, Lock, Pencil, ReceiptText } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Pastille } from '../ui/Primitives';
import { ChampRecherchable } from '../ui/ChampRecherchable';
import { useToast } from '../ui/useToast';
import { ApercuFactures, Rubrique } from './ApercuFactures';
import { FacturationMensuelle } from './FacturationMensuelle';
import { FacturationFinParcours } from './FacturationFinParcours';
import { AvertissementEmetteur, ListeFactures } from './FacturesDuContrat';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import {
  centimesSaisis,
  formaterCentimes,
  montantEnSaisie,
  useEmployeursAccord,
  useEnregistrerAccord,
  type AccordFinancement,
  type EmployeurAccord,
  type FinanceurChoisissable,
  type FinancementDossier,
  type NatureAccord,
  type SaisieAccord,
} from '../../lib/useFinancements';

const CLASSE_CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

/** SIRET lisible, groupé comme sur un Kbis : 123 456 789 00012. */
function siretLisible(siret: string): string {
  const chiffres = siret.replace(/\s/g, '');
  return chiffres.length === 14
    ? `${chiffres.slice(0, 3)} ${chiffres.slice(3, 6)} ${chiffres.slice(6, 9)} ${chiffres.slice(9)}`
    : siret;
}

/**
 * Accord de financement d'un parcours OF (TF-417) : contrat de
 * professionnalisation ou France Travail — qui paie, sous quelle référence, au
 * taux de quel accord, pour combien d'heures, sur quelles dates et, pour un
 * contrat de professionnalisation, chez quel employeur. Les factures aux heures
 * réalisées le lisent : chaque mois pour un contrat de professionnalisation
 * (TF-416, « Facturation mensuelle »), en une fois en fin de parcours pour
 * France Travail (TF-418, « Facture de fin de parcours »), puis « Factures et
 * avoirs ».
 *
 * La plateforme ne porte pas de contrat de professionnalisation : l'accord en
 * garde les dates et l'employeur. Ce qu'il dit — état, ce qui manque, montant
 * maximal, propositions — vient de l'API ; seul l'aperçu du montant pendant la
 * saisie se calcule ici, le temps que l'API le confirme.
 */
export function AccordFinancementOf({
  apprenantId,
  vue,
  financeurs,
  emetteur,
  referentiel,
}: {
  apprenantId: string;
  vue: AccordFinancement;
  financeurs: FinanceurChoisissable[];
  emetteur: FinancementDossier['emetteur'];
  referentiel: FinancementDossier['referentielFacture'];
}): JSX.Element {
  const [edition, setEdition] = useState(false);
  const { accord, dossier } = vue;

  return (
    <section className="space-y-4 p-4 sm:p-6" aria-label="Accord de financement du parcours">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-display break-words font-bold text-codialis-navy">Accord de financement</h4>
          <p className="text-xs text-codialis-gray">
            Parcours OF
            {dossier.session
              ? ` · ${dossier.session.libelle}, du ${jourFrancais(dossier.session.dateDebut)} au ${jourFrancais(
                  dossier.session.dateFin,
                )}`
              : ' · sans session'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {accord && <Pastille texte={accord.natureLibelle} ton="info" />}
          <Pastille texte={vue.situation.libelle} ton={vue.situation.ton} />
        </div>
      </div>

      {edition ? (
        <FormulaireAccord
          apprenantId={apprenantId}
          vue={vue}
          financeurs={financeurs}
          onFermer={() => setEdition(false)}
        />
      ) : (
        <>
          {!accord && (
            <p className="text-sm text-codialis-gray">
              Aucun accord saisi. Un contrat de professionnalisation ou un parcours France Travail se
              facture aux heures réalisées, au taux horaire de son accord et dans la limite de son volume
              d’heures.
            </p>
          )}
          {vue.situation.detail && (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
              {vue.situation.detail}
            </p>
          )}
          {accord && (
            <>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Rubrique libelle="Type de facturation">
                  {accord.natureLibelle}
                  <span className="block text-[11px] font-normal text-codialis-gray">{accord.facturation}</span>
                </Rubrique>
                {accord.nature === 'PROFESSIONNALISATION' && (
                  <Rubrique libelle="Employeur">
                    {accord.employeur ? (
                      <>
                        <span className="break-words">{accord.employeur.denomination}</span>
                        <span className="block font-mono text-[11px] font-normal text-codialis-gray">
                          SIRET {siretLisible(accord.employeur.siret)}
                          {accord.employeur.ville ? ` · ${accord.employeur.ville}` : ''}
                        </span>
                      </>
                    ) : (
                      <span className="text-amber-700">À désigner</span>
                    )}
                  </Rubrique>
                )}
                <Rubrique libelle="Payeur">
                  {accord.payeur === 'FINANCEUR'
                    ? (accord.financeur?.raisonSociale ?? '—')
                    : accord.payeur === 'EMPLOYEUR'
                      ? `${accord.employeur?.denomination ?? 'Employeur'} (paiement direct)`
                      : 'Non renseigné'}
                </Rubrique>
                <Rubrique libelle="Référence de prise en charge">
                  <span className="break-all font-mono">{accord.reference ?? '—'}</span>
                </Rubrique>
                <Rubrique libelle="N° de pro forma">
                  <span className="break-all font-mono">{accord.numeroProForma ?? '—'}</span>
                </Rubrique>
                <Rubrique libelle={accord.libelleDates}>
                  {accord.periode ?? <span className="text-amber-700">À préciser</span>}
                </Rubrique>
              </dl>

              <div className="rounded-2xl border border-gray-100">
                <ul className="divide-y divide-gray-100">
                  <li className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm sm:px-4">
                    <span className="min-w-0 text-codialis-gray">Taux horaire</span>
                    <span className="shrink-0 font-medium tabular-nums text-codialis-navy">
                      {accord.tauxTexte ?? '—'}
                    </span>
                  </li>
                  <li className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm sm:px-4">
                    <span className="min-w-0 text-codialis-gray">
                      Volume d’heures pris en charge
                      {dossier.formation && (
                        <span className="block text-[11px]">
                          Programme de la formation : {dossier.formation.heures} h
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 font-medium tabular-nums text-codialis-navy">
                      {accord.volumeTexte ?? '—'}
                    </span>
                  </li>
                </ul>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-b-2xl bg-codialis-sand px-3 py-3 sm:px-4">
                  <span className="text-sm font-bold text-codialis-navy">Montant maximal pris en charge</span>
                  <span className="font-display text-lg font-extrabold tabular-nums text-codialis-navy">
                    {vue.montantMaximal?.texte ?? '—'}
                  </span>
                  {vue.montantMaximal && (
                    <span className="w-full text-right text-[11px] tabular-nums text-codialis-gray">
                      {vue.montantMaximal.calcul}
                    </span>
                  )}
                </div>
              </div>
              {vue.depassementProgramme && (
                <p className="flex gap-1.5 text-[11px] font-semibold text-amber-700">
                  <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0">{vue.depassementProgramme}</span>
                </p>
              )}

              {vue.apercuFacturation && (
                <ApercuFactures apercu={vue.apercuFacturation} libelleApprenant="Apprenant(e)" />
              )}

              {vue.facturation && <FacturationMensuelle accordId={accord.id} facturation={vue.facturation} />}
              {vue.finParcours && <FacturationFinParcours accordId={accord.id} facturation={vue.finParcours} />}

              {(vue.facturation || vue.finParcours || vue.factures.length > 0) && (
                <section className="rounded-2xl border border-gray-100" aria-label="Factures et avoirs de l’accord">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
                    <h5 className="flex min-w-0 items-center gap-2 text-sm font-bold text-codialis-navy">
                      <ReceiptText className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
                      Factures et avoirs
                      {vue.factures.length > 0 && (
                        <span className="text-xs font-semibold text-codialis-gray">({vue.factures.length})</span>
                      )}
                    </h5>
                  </div>
                  <div className="space-y-3 p-3 sm:p-4">
                    <AvertissementEmetteur emetteur={emetteur} />
                    <ListeFactures
                      factures={vue.factures}
                      modesReglement={referentiel.modesReglement}
                      vide={
                        vue.finParcours
                          ? 'Aucune facture sous cet accord : la facture de fin de parcours se prépare ci-dessus.'
                          : 'Aucune facture sous cet accord : chaque mois se prépare depuis la facturation mensuelle.'
                      }
                    />
                  </div>
                </section>
              )}
            </>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 text-[11px] text-codialis-gray">
              {accord
                ? `Modifié le ${dateHeure(accord.modifieLe)}${accord.modifiePar ? ` par ${accord.modifiePar}` : ''}.`
                : 'Les factures aux heures réalisées liront cet accord.'}
            </p>
            <BoutonSecondaire onClick={() => setEdition(true)}>
              <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
              {accord ? 'Modifier' : 'Saisir l’accord de financement'}
            </BoutonSecondaire>
          </div>
        </>
      )}
    </section>
  );
}

/** Valeur du choix du payeur : un identifiant de financeur, l'employeur, ou personne. */
const EMPLOYEUR = 'EMPLOYEUR';

/** Centièmes d'heure d'un volume saisi (« 152,5 ») ; `null` vide, `undefined` illisible. */
function centiemesSaisis(texte: string): number | null | undefined {
  return centimesSaisis(texte.replace(/h$/i, ''));
}

function payeurDeLaNature(
  nature: NatureAccord,
  employeur: EmployeurAccord | null,
  vue: AccordFinancement,
): string {
  if (nature === 'FRANCE_TRAVAIL') return vue.propositions.financeurFranceTravail?.id ?? '';
  return employeur?.financeurPropose?.id ?? '';
}

function payeurInitial(vue: AccordFinancement, employeur: EmployeurAccord | null): string {
  const { accord } = vue;
  if (accord?.payeur === 'FINANCEUR' && accord.financeur) return accord.financeur.id;
  if (accord?.payeur === 'EMPLOYEUR') return EMPLOYEUR;
  // Première saisie : l'OPCO de l'employeur proposé.
  if (!accord) return payeurDeLaNature('PROFESSIONNALISATION', employeur, vue);
  return '';
}

function FormulaireAccord({
  apprenantId,
  vue,
  financeurs,
  onFermer,
}: {
  apprenantId: string;
  vue: AccordFinancement;
  financeurs: FinanceurChoisissable[];
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const enregistrer = useEnregistrerAccord();
  const employeurs = useEmployeursAccord(apprenantId, true);
  const { accord, propositions } = vue;
  const premiere = !accord;

  const employeurInitial = accord ? accord.employeur : propositions.employeur;
  const [nature, setNature] = useState<NatureAccord>(accord?.nature ?? 'PROFESSIONNALISATION');
  const [employeurId, setEmployeurId] = useState<string | null>(employeurInitial?.id ?? null);
  const [payeur, setPayeur] = useState(() => payeurInitial(vue, employeurInitial));
  const [payeurTouche, setPayeurTouche] = useState(false);
  const [reference, setReference] = useState(accord?.reference ?? '');
  const [numeroProForma, setNumeroProForma] = useState(accord?.numeroProForma ?? '');
  const [taux, setTaux] = useState(montantEnSaisie(accord?.tauxHoraire ?? null));
  const [volume, setVolume] = useState(montantEnSaisie(accord?.volumeHeures ?? null).replace(/,00$/, ''));
  const [debut, setDebut] = useState((accord ? accord.dateDebut : propositions.dateDebut) ?? '');
  const [fin, setFin] = useState((accord ? accord.dateFin : propositions.dateFin) ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  // L'employeur retenu reste dans la liste, même s'il n'est plus rattaché à l'antenne.
  const liste = useMemo(() => {
    const lus = employeurs.data?.employeurs ?? [];
    return employeurInitial && !lus.some((employeur) => employeur.id === employeurInitial.id)
      ? [employeurInitial, ...lus]
      : lus;
  }, [employeurs.data?.employeurs, employeurInitial]);
  const employeur = liste.find((candidat) => candidat.id === employeurId) ?? null;
  const professionnalisation = nature === 'PROFESSIONNALISATION';

  const choisirNature = (valeur: NatureAccord): void => {
    setNature(valeur);
    setErreur(null);
    // Le payeur proposé suit la nature tant qu'on ne l'a pas choisi soi-même.
    if (!payeurTouche) setPayeur(payeurDeLaNature(valeur, valeur === 'PROFESSIONNALISATION' ? employeur : null, vue));
    else if (valeur === 'FRANCE_TRAVAIL' && payeur === EMPLOYEUR) setPayeur('');
  };

  const choisirEmployeur = (id: string): void => {
    setEmployeurId(id);
    setErreur(null);
    const choisi = liste.find((candidat) => candidat.id === id) ?? null;
    if (!payeurTouche && choisi?.financeurPropose) setPayeur(choisi.financeurPropose.id);
  };

  // Aperçu du montant maximal pendant la saisie : l'API le recalcule.
  const tauxLu = centimesSaisis(taux);
  const volumeLu = centiemesSaisis(volume);
  const illisible = tauxLu === undefined || volumeLu === undefined;
  const apercu =
    typeof tauxLu === 'number' && typeof volumeLu === 'number' ? Math.round((tauxLu * volumeLu) / 100) : null;

  const idPrefixe = `accord-${apprenantId}`;
  const propose = (identifiant: string) =>
    !payeurTouche && premiere && identifiant !== '' && payeur === identifiant;

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (illisible) {
      setErreur('Le taux s’écrit en euros (15,00) et le volume en heures (800 ou 152,5).');
      return;
    }
    setErreur(null);
    const saisie: SaisieAccord = {
      nature,
      payeur:
        payeur === ''
          ? null
          : payeur === EMPLOYEUR
            ? { type: 'EMPLOYEUR' }
            : { type: 'FINANCEUR', financeurId: payeur },
      employeurId: professionnalisation ? employeurId : null,
      reference: reference.trim() || null,
      numeroProForma: numeroProForma.trim() || null,
      tauxHoraire: taux.trim() || null,
      volumeHeures: volume.trim() || null,
      dateDebut: debut || null,
      dateFin: fin || null,
    };
    enregistrer.mutate(
      { apprenantId, saisie },
      {
        onSuccess: () => {
          toast('Accord de financement enregistré.');
          onFermer();
        },
        onError: (refus) => {
          const detail = detailsRefus(refus)[0];
          setErreur(
            detail?.message ??
              (refus instanceof ErreurApi ? refus.message : 'L’accord n’a pas pu être enregistré.'),
          );
        },
      },
    );
  };

  return (
    <form onSubmit={soumettre} className="space-y-4" aria-label="Saisie de l’accord de financement">
      <fieldset className="min-w-0">
        <legend className="mb-2 text-xs font-semibold text-codialis-navy">Type de facturation</legend>
        <div role="radiogroup" aria-label="Type de facturation" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {vue.natures.map((option) => {
            const choisie = nature === option.valeur;
            const figee = Boolean(vue.refusNature) && accord?.nature !== option.valeur;
            return (
              <button
                key={option.valeur}
                type="button"
                role="radio"
                aria-checked={choisie}
                disabled={figee || enregistrer.isPending}
                onClick={() => choisirNature(option.valeur)}
                className={`min-h-[44px] min-w-0 rounded-xl border px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  choisie
                    ? 'border-codialis-navy bg-codialis-navy text-white'
                    : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                }`}
              >
                <span className="block text-sm font-bold">{option.libelle}</span>
                <span className={`block text-[11px] ${choisie ? 'text-white/80' : 'text-codialis-gray'}`}>
                  {option.facturation}
                </span>
              </button>
            );
          })}
        </div>
        {vue.refusNature && <p className="mt-1 text-[11px] text-codialis-gray">{vue.refusNature}</p>}
      </fieldset>

      {professionnalisation && (
        <div className="min-w-0">
          <ChampRecherchable
            id={`${idPrefixe}-employeur`}
            libelle="Employeur du contrat de professionnalisation"
            placeholder={employeurs.isLoading ? 'Chargement des entreprises…' : 'Raison sociale, SIRET ou ville…'}
            options={liste.map((candidat) => ({
              id: candidat.id,
              libelle: candidat.denomination,
              detail: [`SIRET ${siretLisible(candidat.siret)}`, candidat.ville].filter(Boolean).join(' · '),
              motsCles: candidat.siret,
            }))}
            valeur={employeurId}
            onChoisir={choisirEmployeur}
            aucunResultat="Aucune entreprise de l’antenne ne correspond."
            desactive={enregistrer.isPending}
            classeLibelle="text-xs font-semibold text-codialis-navy"
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            {premiere && propositions.employeur && employeurId === propositions.employeur.id
              ? 'Proposé d’après l’immersion validée du dossier. '
              : ''}
            Une entreprise absente de l’antenne s’ajoute au répertoire, par la relation entreprise ou
            l’administratif.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor={`${idPrefixe}-debut`} className="mb-1 block text-xs font-semibold text-codialis-navy">
            {professionnalisation ? 'Début du contrat' : 'Début du parcours'}
          </label>
          <input
            id={`${idPrefixe}-debut`}
            type="date"
            value={debut}
            onChange={(e) => {
              setDebut(e.target.value);
              setErreur(null);
            }}
            className={CLASSE_CHAMP}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor={`${idPrefixe}-fin`} className="mb-1 block text-xs font-semibold text-codialis-navy">
            {professionnalisation ? 'Fin du contrat' : 'Fin du parcours'}
          </label>
          <input
            id={`${idPrefixe}-fin`}
            type="date"
            value={fin}
            min={debut || undefined}
            onChange={(e) => {
              setFin(e.target.value);
              setErreur(null);
            }}
            className={CLASSE_CHAMP}
          />
        </div>
        {premiere && propositions.provenanceDates && (
          <p className="text-[11px] text-codialis-gray sm:col-span-2">
            Dates proposées ({propositions.provenanceDates}) : à corriger selon l’accord reçu.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor={`${idPrefixe}-payeur`} className="mb-1 block text-xs font-semibold text-codialis-navy">
            Payeur
          </label>
          <select
            id={`${idPrefixe}-payeur`}
            value={payeur}
            onChange={(e) => {
              setPayeur(e.target.value);
              setPayeurTouche(true);
              setErreur(null);
            }}
            className={CLASSE_CHAMP}
          >
            <option value="">Non renseigné</option>
            <optgroup label="Financeurs">
              {financeurs.map((financeur) => (
                <option key={financeur.id} value={financeur.id}>
                  {financeur.raisonSociale}
                  {financeur.type === 'OPCO' ? '' : ` (${financeur.typeLibelle})`}
                </option>
              ))}
            </optgroup>
            {professionnalisation && employeur && (
              <option value={EMPLOYEUR}>{employeur.denomination} (paiement direct)</option>
            )}
          </select>
          <p className="mt-1 text-[11px] text-codialis-gray">
            {propose(payeur)
              ? professionnalisation
                ? `Proposé d’après l’OPCO de la fiche employeur : « ${employeur?.opco ?? ''} ».`
                : 'Proposé pour un parcours France Travail.'
              : professionnalisation && employeur?.opco
                ? `OPCO déclaré sur la fiche employeur : « ${employeur.opco} ».`
                : professionnalisation
                  ? 'L’OPCO de l’employeur règle le contrat de professionnalisation.'
                  : 'France Travail règle le parcours du demandeur d’emploi.'}
          </p>
        </div>
        <div className="min-w-0">
          <label
            htmlFor={`${idPrefixe}-reference`}
            className="mb-1 block text-xs font-semibold text-codialis-navy"
          >
            Référence de prise en charge
          </label>
          <input
            id={`${idPrefixe}-reference`}
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
              setErreur(null);
            }}
            maxLength={50}
            autoComplete="off"
            spellCheck={false}
            className={`${CLASSE_CHAMP} font-mono`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            {payeur === EMPLOYEUR
              ? 'Facultative quand l’employeur paie lui-même.'
              : 'Numéro de l’accord, porté sur chaque facture adressée au financeur.'}
          </p>
        </div>
        <div className="min-w-0">
          <label
            htmlFor={`${idPrefixe}-pro-forma`}
            className="mb-1 block text-xs font-semibold text-codialis-navy"
          >
            N° de pro forma
          </label>
          <input
            id={`${idPrefixe}-pro-forma`}
            value={numeroProForma}
            onChange={(e) => {
              setNumeroProForma(e.target.value);
              setErreur(null);
            }}
            maxLength={50}
            autoComplete="off"
            spellCheck={false}
            className={`${CLASSE_CHAMP} font-mono`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            Facultatif. Porté sur chaque facture, à côté de la référence de prise en charge.
          </p>
        </div>
      </div>

      <fieldset className="min-w-0">
        <legend className="mb-2 text-xs font-semibold text-codialis-navy">Prise en charge aux heures</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <label htmlFor={`${idPrefixe}-taux`} className="mb-1 block text-[11px] font-semibold text-codialis-gray">
              Taux horaire
            </label>
            <div className="relative">
              <input
                id={`${idPrefixe}-taux`}
                value={taux}
                onChange={(e) => {
                  setTaux(e.target.value);
                  setErreur(null);
                }}
                readOnly={vue.refusTaux !== null}
                aria-describedby={vue.refusTaux ? `${idPrefixe}-taux-fige` : undefined}
                inputMode="decimal"
                autoComplete="off"
                aria-invalid={tauxLu === undefined}
                className={`${CLASSE_CHAMP} pr-12 text-right tabular-nums ${
                  tauxLu === undefined ? 'border-red-300 bg-red-50' : ''
                } ${vue.refusTaux ? 'cursor-not-allowed text-codialis-gray' : ''}`}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-codialis-gray"
              >
                €/h
              </span>
            </div>
            {vue.refusTaux ? (
              <p id={`${idPrefixe}-taux-fige`} className="mt-1 flex gap-1.5 text-[11px] text-codialis-gray">
                <Lock className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="min-w-0">{vue.refusTaux}</span>
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-codialis-gray">
                Requis pour facturer : les heures se facturent à ce taux.
              </p>
            )}
          </div>
          <div className="min-w-0">
            <label htmlFor={`${idPrefixe}-volume`} className="mb-1 block text-[11px] font-semibold text-codialis-gray">
              Volume d’heures
            </label>
            <div className="relative">
              <input
                id={`${idPrefixe}-volume`}
                value={volume}
                onChange={(e) => {
                  setVolume(e.target.value);
                  setErreur(null);
                }}
                inputMode="decimal"
                autoComplete="off"
                aria-invalid={volumeLu === undefined}
                className={`${CLASSE_CHAMP} pr-8 text-right tabular-nums ${
                  volumeLu === undefined ? 'border-red-300 bg-red-50' : ''
                }`}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-codialis-gray"
              >
                h
              </span>
            </div>
            {vue.dossier.formation && (
              <p className="mt-1 text-[11px] text-codialis-gray">
                Programme de la formation : {vue.dossier.formation.heures} h.
              </p>
            )}
          </div>
        </div>
        <p className="mt-2 flex gap-1.5 text-[11px] text-codialis-gray">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">
            Les heures réalisées se facturent à ce taux, sans forfait, et leur cumul ne dépasse pas le volume
            pris en charge. Un champ laissé vide est inconnu, pas nul.
          </span>
        </p>
      </fieldset>

      <div
        className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-2xl bg-codialis-sand px-3 py-3 sm:px-4"
        aria-live="polite"
      >
        <span className="text-sm font-bold text-codialis-navy">Montant maximal pris en charge</span>
        <span className="font-display text-lg font-extrabold tabular-nums text-codialis-navy">
          {illisible ? 'Saisie illisible' : apercu === null ? '—' : formaterCentimes(apercu)}
        </span>
      </div>

      {erreur && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <BoutonSecondaire onClick={onFermer} disabled={enregistrer.isPending}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={enregistrer.isPending}>
          {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonPrincipal>
      </div>
    </form>
  );
}
